import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ClarityModule } from '@clr/angular';

/**
 * Phase 3.2/3.3 — operator-facing editor for the v2 voxtral correction-context shape.
 *
 * Reduced 2026-08 with the LLM-corrector removal: the editor now only manages
 * the Keywords / Listen placeholders that feed the fuzzy and phonetic
 * correctors. The former prompt-markdown, per-language override, catalog
 * (S3) and Stichwörter sections are gone; `prompt` / `languages` /
 * `correction_languages` keys in legacy JSON are ignored on load and dropped
 * on the next save.
 *
 * v2 JSON contract (matches voxtral_server.correction.context.parse):
 *   {
 *     "<placeholder1>": ["...", "..."],
 *     "<placeholder2>": ["...", "..."]
 *   }
 *
 * The same component is used from both details.component (per-broadcast panel)
 * and voxtral-schedule-dialog (table-row drawer). Each parent passes the
 * broadcast's current JSON in and listens for `applyToBroadcast` to persist
 * via POST /broadcast/voxtral-correction-context.
 */

interface PlaceholderRow {
  name: string;
  // Operator types comma-separated values; we parse back to string[] on save.
  csv: string;
}

@Component({
  selector: 'app-voxtral-correction-context-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, ClarityModule],
  templateUrl: './voxtral-correction-context-editor.component.html',
  styleUrl: './voxtral-correction-context-editor.component.scss',
})
export class VoxtralCorrectionContextEditorComponent implements OnInit, OnChanges {
  /** Current broadcast's correction_context JSON (legacy or v2). Two-way: edits stay local until Apply. */
  @Input() currentJson = '';
  /** Disabled state — propagated to all inputs while a save is in-flight upstream. */
  @Input() saving = false;

  /** Fires when the operator clicks "Apply to broadcast" — parent persists via POST /broadcast/voxtral-correction-context. */
  @Output() applyToBroadcast = new EventEmitter<string>();

  // ---- Local state ----
  placeholders: PlaceholderRow[] = [];
  errorMessage = '';
  // 2026-06-04 — non-blocking notice (e.g. "legacy CSV converted to players[]")
  // shown above the form. Clears on any successful re-parse.
  infoMessage = '';
  successMessage = '';

  // Validation
  /** Reserved placeholder names mirror voxtral_server.correction.context.RESERVED_NAMES — block as operator-defined to keep behaviour predictable. */
  private static readonly RESERVED = new Set(['LANGUAGE', 'ORIGINAL', 'FUZZY', 'TIMESTAMP']);
  private static readonly NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

  ngOnInit(): void {
    this.parseIncoming(this.currentJson);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentJson'] && !changes['currentJson'].firstChange) {
      this.parseIncoming(this.currentJson);
    }
  }

  // ---- Parsing / serialising the v2 shape ----

  /**
   * Accept legacy shapes too (parse() in voxtral_server.correction.context
   * tolerates them). Reverse-mapping legacy → v2 here keeps the operator's
   * mental model unified: even an old broadcast row populates the editor in
   * v2 form. Saving back writes the v2 canonical shape.
   */
  private parseIncoming(raw: string): void {
    this.placeholders = [];
    this.errorMessage = '';
    this.infoMessage = '';
    const s = (raw || '').trim();
    if (!s) return;
    let data: any;
    try {
      data = JSON.parse(s);
    } catch {
      // Legacy CSV roster (e.g. operator pasted "Player A, Player B" or an
      // upstream enrichment Lambda wrote a comma-list of Wikipedia
      // categories). Treat the whole string as a `players` placeholder so
      // the editor still renders; the next Save converts it to the V2
      // canonical JSON shape.
      this.placeholders = [{ name: 'players', csv: s }];
      this.infoMessage =
        'Legacy CSV erkannt — beim nächsten Speichern wird der Inhalt als players[] in die V2-JSON-Struktur konvertiert.';
      return;
    }
    if (typeof data !== 'object' || data === null || Array.isArray(data)) {
      this.errorMessage = 'JSON muss auf oberster Ebene ein Objekt sein.';
      return;
    }
    // Legacy `type`/`description` survives parse but isn't displayed — the
    // next Save writes the canonical v2 shape. The retired LLM-era keys
    // (`prompt`, `languages`, `correction_languages`) are likewise skipped
    // and intentionally dropped on the next save.
    const skipKeys = new Set([
      'prompt',
      'type',
      'description',
      'languages',
      'correction_languages',
    ]);
    for (const [k, v] of Object.entries(data)) {
      if (skipKeys.has(k)) continue;
      if (!Array.isArray(v)) continue;
      const csv = v.map((it) => this.itemToString(it)).filter((x) => x.length > 0).join(', ');
      this.placeholders.push({ name: k, csv });
    }
  }

  private itemToString(item: any): string {
    if (typeof item === 'string') return item.trim();
    // Legacy teams entries are `{name, aliases, players}` objects; surface
    // just the `name` so the operator sees a flat list. Aliases/players are
    // lossy here — for the new shape operators should split into separate
    // placeholders like `{{teams}}` + `{{players}}`.
    if (typeof item === 'object' && item && typeof item.name === 'string') return item.name.trim();
    return '';
  }

  /** 2026-06-10 — split a list-input string on any run of commas, newlines,
   * or carriage returns. Accepts pure CSV (`"a, b, c"`), pure LF (`"a\nb\nc"`),
   * pure CRLF (`"a\r\nb\r\nc"`), and any mix (`"a,b\nc"`). Runs of delimiters
   * collapse via the `+` quantifier; trimming + non-empty filter drops phantom
   * entries. Display round-trip (load → `.join(', ')`) keeps CSV as the canonical
   * display, but operators can paste either format on every edit. */
  static parseListInput(raw: string): string[] {
    return (raw || '')
      .split(/[,\r\n]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }

  /** Build the v2-canonical JSON for apply: just the placeholder lists. */
  toJson(): string {
    const out: any = {};
    for (const row of this.placeholders) {
      const name = (row.name || '').trim();
      if (!name) continue;
      out[name] = VoxtralCorrectionContextEditorComponent.parseListInput(row.csv || '');
    }
    return JSON.stringify(out, null, 2);
  }

  // ---- Placeholder list editing ----

  addPlaceholder(): void {
    // Default name — first unused among common ones.
    const candidates = ['terms', 'teams', 'players', 'products', 'cities', 'list'];
    const used = new Set(this.placeholders.map((p) => p.name.toLowerCase()));
    const fresh = candidates.find((c) => !used.has(c)) || `list${this.placeholders.length + 1}`;
    this.placeholders.push({ name: fresh, csv: '' });
  }

  removePlaceholderByRow(row: PlaceholderRow): void {
    const idx = this.placeholders.indexOf(row);
    if (idx >= 0) this.placeholders.splice(idx, 1);
  }

  /**
   * Lightweight validation feedback (operator can still save invalid names;
   * the controller validates more strictly server-side). Returns null when ok.
   */
  validatePlaceholderName(name: string): string | null {
    const n = (name || '').trim();
    if (!n) return 'Name darf nicht leer sein.';
    if (!VoxtralCorrectionContextEditorComponent.NAME_RE.test(n))
      return `Name "${n}" ist ungültig. Erlaubt: Buchstaben/Ziffern/Unterstrich, erstes Zeichen kein Ziffer.`;
    if (VoxtralCorrectionContextEditorComponent.RESERVED.has(n.toUpperCase()))
      return `Name "${n}" ist reserviert.`;
    return null;
  }

  // ---- Apply to the broadcast at hand ----

  onApplyToBroadcast(): void {
    const json = this.toJson();
    if (!json || json === '{}') {
      // Empty payload clears the broadcast's context — confirm with the operator
      // so an accidental delete-all doesn't silently land.
      if (!confirm('Der Korrektur-Kontext wird auf dieser Sendung geleert. Fortfahren?')) return;
    }
    this.applyToBroadcast.emit(json);
  }
}
